/**
 * BankConnectionStatusBadge
 * Colored status pill for a Plaid bank connection's health (#563). Mirrors the
 * calendar `ConnectionStatusBadge`, but keyed on the Plaid lifecycle enum
 * `{Active, LoginRequired, Disconnected}` — NOT calendar's token states.
 */

import { BankConnectionStatus } from '@signalvote/shared';
import { cn } from '@/lib/utils';

interface BankConnectionStatusBadgeProps {
  status: BankConnectionStatus;
  className?: string;
}

const statusConfig: Record<
  BankConnectionStatus,
  { label: string; bgClass: string; textClass: string }
> = {
  [BankConnectionStatus.Active]: {
    label: 'Active',
    bgClass: 'bg-green-100',
    textClass: 'text-green-800',
  },
  [BankConnectionStatus.LoginRequired]: {
    label: 'Needs sign-in',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-800',
  },
  [BankConnectionStatus.Disconnected]: {
    label: 'Disconnected',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-700',
  },
};

export function BankConnectionStatusBadge({ status, className }: BankConnectionStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        config.bgClass,
        config.textClass,
        className,
      )}
    >
      {config.label}
    </span>
  );
}

export default BankConnectionStatusBadge;
